import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useRef } from 'react';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Wraps a Mui Dialog to provide some simple logic and layout
 *
 * @example
 * ```
 * <DialogWrapper
 *  header={"Fancy dialog"}
 *  open={true}
 *  setClosed={(action) => console.log("Closed dialog via:", action ? "action button." : "close button.")}
 *  action={"Select"}
 *  actionIcon={<SelectIcon />}
 * >
 *  <Typography>Hello, world!</Typography>
 * </DialogWrapper>
 * ```
 */
export const DialogWrapper = ({ header, open, setClosed, action, disableAction, actionIcon, children, closeIcon, wrappedElement, save = false, captureClicks = false, fullWidth = true, maxWidth = 'lg', ...others }) => {
    'use no memo';
    // Error: Cannot access refs during render
    const ref = useRef(null);
    const handleClose = (event) => {
        debug && console.log('handleCancel', event);
        setClosed(false);
    };
    const handleSave = (event) => {
        debug && console.log('handleSave', event);
        setClosed(true);
    };
    useEffect(() => {
        const listener = (event) => {
            if (!open)
                return;
            if (event.key === 'Escape') {
                if (event.target === ref.current)
                    handleClose(event);
            }
        };
        document.addEventListener('keydown', listener, true);
        return () => document.removeEventListener('keydown', listener, true);
    }, [open]);
    return (_jsxs(Dialog, { open: open, onClick: (event) => captureClicks && event.stopPropagation(), "aria-labelledby": "alert-dialog-title", "aria-describedby": "alert-dialog-description", ...others, fullWidth,
        maxWidth,
        // this stops mui adding padding-right (15px) to body tag and causing a gap to the right of the menu
        // https://stackoverflow.com/questions/60682426/material-ui-adds-padding-to-the-body-tag-when-a-dialog-is-opened
        disableScrollLock: true, slotProps: { container: { ref } }, children: [_jsxs(DialogTitle, { id: "alert-dialog-title", sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }, children: [typeof header === 'string' && header.length ? header : header, _jsx("div", { children: _jsx(Button, { variant: "contained", color: "warning", onClick: handleClose, endIcon: _jsx(CloseIcon, {}), children: "Exit" }) })] }), _jsx(DialogContent, { children: children ?? wrappedElement }), (action || save) && (_jsx("div", { style: { marginLeft: 'auto', marginRight: 0 }, children: _jsx(Box, { sx: { paddingRight: 3, paddingBottom: 2 }, children: _jsx(Button, { variant: 'contained', disabled: disableAction, onClick: handleSave, endIcon: actionIcon ?? closeIcon ?? _jsx(SaveIcon, {}), children: action ?? 'Select' }) }) }))] }));
};
